import React, { useEffect, useState } from 'react';
import { CreditCard, Plus, Pencil, Trash2, Star, Loader2, X } from 'lucide-react';
import { insurance as insuranceApi } from '../utils/api';

const INSURANCE_TYPES = [
  { value: 'medical', label: 'Medical' },
  { value: 'dental', label: 'Dental' },
  { value: 'vision', label: 'Vision' },
  { value: 'pharmacy', label: 'Pharmacy' }
];

const RELATIONSHIP_OPTIONS = ['self', 'spouse', 'child', 'other'];

const defaultForm = {
  providerName: '',
  planName: '',
  insuranceType: 'medical',
  memberId: '',
  groupNumber: '',
  policyNumber: '',
  subscriberName: '',
  subscriberRelationship: 'self',
  effectiveDate: '',
  terminationDate: '',
  phone: '',
  claimsAddress: '',
  isPrimary: false
};

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

export default function InsuranceSection() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadPolicies = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await insuranceApi.list();
      setPolicies(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load insurance policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(defaultForm);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (policy) => {
    setEditingId(policy.id);
    setForm({
      providerName: policy.providerName || '',
      planName: policy.planName || '',
      insuranceType: policy.insuranceType || 'medical',
      memberId: policy.memberId || '',
      groupNumber: policy.groupNumber || '',
      policyNumber: policy.policyNumber || '',
      subscriberName: policy.subscriberName || '',
      subscriberRelationship: policy.subscriberRelationship || 'self',
      effectiveDate: formatDate(policy.effectiveDate),
      terminationDate: formatDate(policy.terminationDate),
      phone: policy.phone || '',
      claimsAddress: policy.claimsAddress || '',
      isPrimary: policy.isPrimary || false
    });
    setFormError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.providerName.trim() || !form.memberId.trim()) {
      setFormError('Insurance provider name and member ID are required.');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const updated = await insuranceApi.update(editingId, form);
        setPolicies(prev => prev.map(p => p.id === editingId ? updated : p));
      } else {
        const created = await insuranceApi.create(form);
        setPolicies(prev => [...prev, created]);
      }
      setShowForm(false);
      // Reload to get correct isPrimary state
      await loadPolicies();
    } catch (err) {
      setFormError(err.message || 'Failed to save insurance policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this insurance policy?')) return;
    try {
      await insuranceApi.remove(id);
      setPolicies(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      setError(err.message || 'Failed to delete insurance policy');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-slate-600" />
          Insurance
        </h3>
        <button
          onClick={openAdd}
          className="px-3 py-1.5 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" />
          Add Policy
        </button>
      </div>

      {error && (
        <div className="bg-red-50/80 border border-red-200/60 text-red-600 px-4 py-3 rounded-xl text-sm mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : policies.length === 0 ? (
        <div className="text-center py-6">
          <CreditCard className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No insurance policies added yet.</p>
          <p className="text-xs text-slate-400 mt-1">Add your insurance information to share with providers during workflows.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {policies.map(policy => (
            <div key={policy.id} className="border border-slate-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900">{policy.providerName}</p>
                    {policy.isPrimary && (
                      <span className="flex items-center gap-1 text-[11px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        <Star className="w-3 h-3" />
                        Primary
                      </span>
                    )}
                    <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full capitalize">
                      {policy.insuranceType}
                    </span>
                  </div>
                  {policy.planName && (
                    <p className="text-sm text-slate-600 mt-0.5">{policy.planName}</p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                    <span>Member ID: {policy.memberId}</span>
                    {policy.groupNumber && <span>Group: {policy.groupNumber}</span>}
                    {policy.effectiveDate && (
                      <span>Effective: {new Date(policy.effectiveDate).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEdit(policy)}
                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(policy.id)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-4">
          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="px-7 py-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                {editingId ? 'Edit Insurance Policy' : 'Add Insurance Policy'}
              </h3>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-7 space-y-5">
              {formError && (
                <div className="bg-red-50/80 border border-red-200/60 text-red-600 px-4 py-3 rounded-2xl text-sm">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Insurance Provider *</label>
                  <input
                    type="text"
                    value={form.providerName}
                    onChange={e => setForm(f => ({ ...f, providerName: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                    placeholder="e.g., Blue Cross Blue Shield"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Plan Name</label>
                  <input
                    type="text"
                    value={form.planName}
                    onChange={e => setForm(f => ({ ...f, planName: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                    placeholder="e.g., PPO Gold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Type</label>
                  <select
                    value={form.insuranceType}
                    onChange={e => setForm(f => ({ ...f, insuranceType: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                  >
                    {INSURANCE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Member ID *</label>
                  <input
                    type="text"
                    value={form.memberId}
                    onChange={e => setForm(f => ({ ...f, memberId: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Group Number</label>
                  <input
                    type="text"
                    value={form.groupNumber}
                    onChange={e => setForm(f => ({ ...f, groupNumber: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Policy Number</label>
                  <input
                    type="text"
                    value={form.policyNumber}
                    onChange={e => setForm(f => ({ ...f, policyNumber: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Subscriber Name</label>
                  <input
                    type="text"
                    value={form.subscriberName}
                    onChange={e => setForm(f => ({ ...f, subscriberName: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                    placeholder="Policy holder name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Relationship</label>
                  <select
                    value={form.subscriberRelationship}
                    onChange={e => setForm(f => ({ ...f, subscriberRelationship: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                  >
                    {RELATIONSHIP_OPTIONS.map(r => (
                      <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Effective Date</label>
                  <input
                    type="date"
                    value={form.effectiveDate}
                    onChange={e => setForm(f => ({ ...f, effectiveDate: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Termination Date</label>
                  <input
                    type="date"
                    value={form.terminationDate}
                    onChange={e => setForm(f => ({ ...f, terminationDate: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Phone</label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                    placeholder="Claims phone number"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Claims Address</label>
                  <input
                    type="text"
                    value={form.claimsAddress}
                    onChange={e => setForm(f => ({ ...f, claimsAddress: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm"
                    placeholder="Claims mailing address"
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 text-sm text-slate-600 bg-slate-50 p-3.5 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  checked={form.isPrimary}
                  onChange={e => setForm(f => ({ ...f, isPrimary: e.target.checked }))}
                  className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="font-medium">Primary insurance</span>
              </label>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={saving}
                  className="flex-1 px-5 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-5 py-3 bg-gradient-to-r from-brand-600 to-violet-600 text-white rounded-xl text-sm font-medium hover:shadow-glow-blue transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : editingId ? 'Save Changes' : 'Add Policy'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
